import React from "react";

import { Metric } from "~/models/metric";
import { concat } from "~/tools/concat";
import { filter } from "~/tools/filter";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { isPresent } from "~/tools/is-present";
import { map } from "~/tools/map";
import { pipe } from "~/tools/pipe";
import { prop } from "~/tools/prop";
import type { Template } from "~/tools/template";
import { TemplateVariablesBoundary } from "~/tools/template-variables/boundary";
import { type TemplateVariableDefinition, TemplateVariableDefinitions } from "~/tools/template-variables/definitions";
import { uniqueBy } from "~/tools/unique-by";
import { useDelayed } from "~/tools/use-delayed";
import { useMemo } from "~/tools/use-memo";

import type { TimeRange } from "../../../app/state/view/timeline/TimeRange";
import type { DashboardListVariable, DashboardVariable, DashboardVariableSpec } from "../../../common/dashboard-spec";
import type { InstantPromQLResult } from "../../../queries/metrics/batch";
import { queryInstantPromQL } from "../../../queries/metrics/batch";
import { queryMetricsLabelValues, queryMetricsLabels } from "../../../queries/metrics/labels";
import { type UseFullQueryOptions, useQueries } from "../../../queries/react-query";

import { generateVariableNames } from "./generate-variable-names";

const DEBOUNCE_VARIABLES_CHANGE_MS = 300;

function multipleValuesToString(values: ReadonlyArray<string>): string {
	return `(${values.join("|")})`;
}

interface VariableConfig<TStatic extends boolean, TValue> {
	isStatic: TStatic;
	name: string;
	value: TValue;
}

type DynamicListPlugin = Exclude<DashboardListVariable["plugin"], { kind: "StaticList" }>;

/**
 * The function converts a DashboardVariable into a VariableConfig,
 * so that it can be used to build template variable definitions
 * out of static values and query dynamic ones.
 */
function variableToConfig(
	name: string,
	{ kind, spec }: DashboardVariableSpec,
): VariableConfig<true, string> | VariableConfig<false, DynamicListPlugin> {
	if (kind === "TextVariable") {
		return { isStatic: true, name, value: spec.value };
	}

	if (spec.defaultValue == null) {
		return { isStatic: true, name, value: "" };
	}

	if (spec.defaultValue.type === "Value") {
		return { isStatic: true, name, value: spec.defaultValue.value };
	}

	if (spec.defaultValue.type === "Values") {
		const value = multipleValuesToString(spec.defaultValue.values);

		return { isStatic: true, name, value };
	}

	// Handle type === "Everything"
	if (spec.customAllValue != null) {
		return { isStatic: true, name, value: spec.customAllValue };
	}

	// The static list does not require querying
	if (spec.plugin.kind === "StaticList") {
		const value = multipleValuesToString(spec.plugin.spec.values.map(prop("value")));

		return { isStatic: true, name, value };
	}

	// The rest of the lists require querying
	return { isStatic: false, name, value: spec.plugin };
}

function queryDynamicVariable(
	timeRange: TimeRange,
	name: string,
	plugin: DynamicListPlugin,
	interpolate: (template: Template<["${", "}"], null>) => undefined | string,
): UseFullQueryOptions<unknown, Error, [string, string]> {
	const select = (values: ReadonlyArray<string>): [string, string] => [name, multipleValuesToString(values)];

	if (plugin.kind === "MetricLabelNames") {
		return {
			select,
			...queryMetricsLabels({
				matches: pipe(plugin.spec.matchers, map(interpolate), filter(isPresent)),
				timeRange,
			}),
		} satisfies UseFullQueryOptions<ReadonlyArray<string>, Error, [string, string]>;
	}

	if (plugin.kind === "MetricLabelValues") {
		return {
			select,
			enabled: plugin.spec.labelName.length > 0,
			...queryMetricsLabelValues({
				label: plugin.spec.labelName,
				matches: pipe(plugin.spec.matchers, map(interpolate), filter(isPresent)),
				timeRange,
			}),
		} satisfies UseFullQueryOptions<ReadonlyArray<string>, Error, [string, string]>;
	}

	const query = interpolate(plugin.spec.expr) ?? "";

	return {
		select: (result) => {
			const values = result
				.transform((vectors) => Metric.extractLabelValuesByName(plugin.spec.labelName, vectors.map(prop("metric"))))
				.getOrElse([]);

			return select(values);
		},
		enabled: query.length > 0,
		...queryInstantPromQL({
			step: undefined,
			query,
			timestamp: timeRange.end,
		}),
	} satisfies UseFullQueryOptions<InstantPromQLResult, Error, [string, string]>;
}

/**
 * The hook converts dashboard variables into template variable definitions,
 * resolving dynamic variables by querying their values when default "Everything" is used.
 */
function useCreateTemplateVariablesDefinitions(
	variables: ReadonlyArray<DashboardVariable>,
	timeRange: TimeRange,
	templateVariables: TemplateVariableDefinitions,
): TemplateVariableDefinitions {
	// First, split dynamic variables from all.
	const [allEntries, dynamicConfigs] = useMemo(() => {
		const all = variables.map(({ name, spec }) => variableToConfig(name, spec));

		return [
			// all entries with `null` placeholders for dynamic variables
			all.map(({ isStatic, name, value }): [string, null | string] => [name, isStatic ? value : null]),

			all.filter((config): config is VariableConfig<false, DynamicListPlugin> => !config.isStatic),
		] as const;
	}, [variables]);

	// Then, query the dynamic variables.
	const queries = useQueries(
		dynamicConfigs.map(({ name, value }) => ({
			refetchOnMount: false,
			refetchOnWindowFocus: false,
			keepPreviousData: true,
			...queryDynamicVariable(timeRange, name, value, (template) => {
				return template.interpolate(templateVariables.values, {
					// do not send partially interpolated templates
					whenVariableMissing: undefined,
					// do not self-reference the variable being queried
					omit: generateVariableNames(name),
				});
			}),
		})),
	);

	// Then, merge dynamic variables results with static ones.
	const entries = pipe(
		queries,
		map(prop("data")),
		filter(isPresent),
		concat(allEntries),
		// dynamic loaded variables should override the `null` placeholders
		uniqueBy(prop(0)),
	);

	return useMemo(
		() => {
			// Finally, convert static + dynamic entries into template variable definitions.
			const definitions = entries.flatMap(
				([name, value]): Array<TemplateVariableDefinition> =>
					generateVariableNames(name).map((variableName) => ({ name: variableName, value, detail: "variable" })),
			);

			return new TemplateVariableDefinitions(definitions);
		},
		[entries],
		[isDeepEqual],
	);
}

export const DashboardVariablesBoundary: React.FC<{
	variables: ReadonlyArray<DashboardVariable>;
	timeRange: TimeRange;
	children: React.ReactNode;
}> = ({ variables, timeRange, children }) => {
	const delayedVariables = useDelayed(variables, DEBOUNCE_VARIABLES_CHANGE_MS, {
		compare: isDeepEqual,
	});

	/**
	 * A variable definition can refer to other variables in its query,
	 * so it should accumulate the already resolved variables to use them for interpolation.
	 */
	const [templateVariables, setTemplateVariables] = React.useState(() => new TemplateVariableDefinitions([]));

	const definitions = useCreateTemplateVariablesDefinitions(
		// debounce the variables change to avoid unnecessary requests/re-renders while the user is changing them
		delayedVariables.length > 0 && variables.length > 0 ? delayedVariables : variables,
		timeRange,
		templateVariables,
	);

	React.useEffect(() => {
		setTemplateVariables(definitions);
	}, [definitions]);

	return <TemplateVariablesBoundary variables={definitions}>{children}</TemplateVariablesBoundary>;
};
